import axios from 'axios';
import ReactGA from 'react-ga4';

import api from '@/sevices/api';
import { withTimingTrack } from '@/utils/analytics';

import {
  JustificativaStatus,
  ProtocoloExecutado,
  ProtocoloExecutadoEmpresa,
  ProtocoloExecutadoResumo,
} from '@/models/ProtocoloExecutado';

import { runtimePathPrefix } from '@/features/runtime/utils/helpers';
import {
  AbrirProtocoloB2BPayload,
  AbrirProtocoloPayload,
  CriarExecucaoPayload,
  ExecutarPassoPayload,
  GetFlowchartResponse,
  RedirecionarFluxoAPIPayload,
  RedirecionarFluxoPayload,
  SetIdentificadoresProtocoloPayload,
  SetIdentificadoresProtocoloResponse,
} from '../types';

function sendOpeningTiming(protocolo: ProtocoloExecutado, timingValue: number) {
  ReactGA.send({
    hitType: 'timing',
    timingCategory: 'Execução de protocolos',
    timingVar: protocolo.codigo,
    timingValue,
    timingLabel: 'Abertura de protocolo',
  });
}

interface GetExecucaoParams {
  expand?: string[];
  omit?: string[];
}

async function getExecucao(
  execucaoID: string,
  params: GetExecucaoParams = {},
  sessionID?: string,
): Promise<ProtocoloExecutado> {
  const execucao = (
    await api.get<ProtocoloExecutado>(
      `${runtimePathPrefix({
        session_id: sessionID,
      })}/protocolos-executados/${execucaoID}`,
      {
        params,
      },
    )
  ).data;
  return execucao;
}

async function getExecucaoResumo(
  execucaoID: string,
  sessionID?: string,
): Promise<ProtocoloExecutadoResumo> {
  const execucao = (
    await api.get<ProtocoloExecutadoResumo>(
      `${runtimePathPrefix({
        session_id: sessionID,
      })}/protocolos-executados/${execucaoID}/resumo`,
    )
  ).data;
  return execucao;
}

async function criarExecucao({
  protocoloId,
}: CriarExecucaoPayload): Promise<ProtocoloExecutado> {
  return (
    await api.post<ProtocoloExecutado>(
      `/protocolos/${protocoloId}/criar-execucao/`,
      {},
    )
  ).data;
}

async function criarExecucaoB2B({
  codigoProtocolo,
  data,
}: AbrirProtocoloB2BPayload): Promise<ProtocoloExecutadoEmpresa> {
  return (
    await api.post<ProtocoloExecutadoEmpresa>(
      `/portal/protocolos/${codigoProtocolo}/abrir/`,
      data,
    )
  ).data;
}

async function abrirProtocolo({
  codigoProtocolo,
}: AbrirProtocoloPayload): Promise<ProtocoloExecutado> {
  return (
    await api.post<ProtocoloExecutado>(`/protocolos-executados/`, {
      codigo_protocolo: codigoProtocolo,
    })
  ).data;
}

async function executarPasso({
  passoExecutado,
  conduta: { respostas = {} } = {},
}: ExecutarPassoPayload): Promise<ProtocoloExecutado> {
  const protocolo = (
    await api.put<ProtocoloExecutado>(
      `${runtimePathPrefix(passoExecutado)}/passos-executados/${
        passoExecutado.id
      }/`,
      respostas,
    )
  ).data;
  return protocolo;
}

async function redirecionarFluxo({
  passoExecutado,
  opcoes,
}: RedirecionarFluxoPayload): Promise<ProtocoloExecutado> {
  // Sugere selecionados ao prontuário forçadamente ou em caso de multiplas opções
  const codes = opcoes.map(({ codigo }) => codigo);
  const serviceURL = passoExecutado.redirecionar_protocolo_url;

  const payloadAPI: RedirecionarFluxoAPIPayload = {
    codigo_protocolo_destino: codes,
  };

  return (await api.post<ProtocoloExecutado>(serviceURL, payloadAPI)).data;
}

async function getFlowchartProtocolo(
  protocoloExecutado: ProtocoloExecutado,
): Promise<string> {
  try {
    const { content } = (
      await api.get<GetFlowchartResponse>(
        `${runtimePathPrefix(protocoloExecutado)}/protocolos-executados/${
          protocoloExecutado.id
        }/fluxograma/`,
      )
    ).data;

    return content;
  } catch (err) {
    return '';
  }
}

interface ProtocoloExecutadoTags {
  tags: {
    nome: string;
    valor: string;
  }[];
}

async function setProcoloExecutadoTags({
  protocoloExecutadoId,
  tags,
}: SetIdentificadoresProtocoloPayload): Promise<ProtocoloExecutadoTags> {
  const { data } = (
    await api.patch<SetIdentificadoresProtocoloResponse>(
      `/protocolos-executados/${protocoloExecutadoId}/tags/`,
      { tags },
    )
  ).data;

  return data;
}

async function cancelarExecucao(
  protocolo: ProtocoloExecutado,
): Promise<boolean> {
  if (protocolo.status === 'CANCELADO') {
    return false;
  }

  try {
    await api.post<ProtocoloExecutado>(
      `${runtimePathPrefix(protocolo)}/protocolos-executados/${
        protocolo.id
      }/cancelar/`,
    );
    return true;
  } catch {
    return false;
  }
}

function cancelarExecucaoSilently(protocolo: ProtocoloExecutado): boolean {
  if (navigator.sendBeacon && protocolo.status !== 'CANCELADO') {
    const baseURL = process.env.REACT_APP_API_URL;
    return navigator.sendBeacon(
      `${baseURL}/protocolos-executados/${protocolo.id}/cancelar/`,
    );
  }

  return false;
}

async function justificarStatusProtocolo(
  protocolo: ProtocoloExecutado,
  justificativa: { texto?: string; modelo?: number },
): Promise<JustificativaStatus | null> {
  try {
    const { modelo, texto } = justificativa;

    const { justificativa_status } = (
      await api.post<{ justificativa_status: JustificativaStatus }>(
        `${runtimePathPrefix(protocolo)}/protocolos-executados/${
          protocolo.id
        }/justificativas/`,
        {
          modelo,
          texto,
        },
      )
    ).data;

    return justificativa_status;
  } catch (err) {
    // Ignora erros de requisição ou servidor p/ não bloquear usuário e evitar
    // cláusulas try/cacth dentro de componentes sem necessidade
    if (axios.isAxiosError(err)) {
      return null;
    }

    throw err;
  }
}

async function pausarExecucao(
  protocolo: ProtocoloExecutado,
): Promise<ProtocoloExecutado> {
  return (
    await api.post<ProtocoloExecutado>(
      `${runtimePathPrefix(protocolo)}/protocolos-executados/${
        protocolo.id
      }/pausar/`,
      {},
    )
  ).data;
}

export default {
  criarExecucao,
  criarExecucaoB2B,
  getExecucao,
  getExecucaoResumo,
  abrirProtocolo: withTimingTrack(abrirProtocolo, sendOpeningTiming),
  executarPasso,
  redirecionarFluxo,
  getFlowchartProtocolo,
  setProcoloExecutadoTags,
  cancelarExecucao,
  cancelarExecucaoSilently,
  justificarStatusProtocolo,
  pausarExecucao,
};
